/**
 * The layout generator (TD §7.1), a port of the prototype's generatePlan + placeFacilities + fillHabitats that
 * writes a PlanDoc with structural junctions (every road meets at the shared plaza dot) and packed zones with
 * shared walls. Deterministic: the same options, species and themes give the same doc, ids included.
 */
import { emptyPlan } from '../../shared/plan-schema';
import { speciesNeeds } from '../../shared/species-needs';
import type { AreaId, GenerateOptions, IconType, NodeId, OtherAreaKind, PlanDoc, Pt, RoadKind, Species, SpeciesPick, Theme } from '../../shared/types';
import { bbox, centroid, clamp, cumLengths, distPoly, pip, pointAlong, polyArea, roadPoly, riverPoly, spline } from '../geom';
import { rng, type Rng } from '../raster';
import { placeFacilities, shopCount } from './facilities';
import { areaRing, fillZones, idMaker, plotRing, type IdMaker, type ZoneItem } from './fill';
import { blob, leaveDist, makeLoop, makePlot, placeEntrance, pushOutOf } from './layout';
import { groupPicks } from './themes';

/** Largest animal count an area may hold (plan schema). */
const MAX_COUNT = 500;

/** Small builder around a plain doc with deterministic ids. */
class Builder {
  constructor(readonly doc: PlanDoc, readonly ids: IdMaker) {}
  vert(p: Pt): string { const id = this.ids(this.doc.verts, 'v'); this.doc.verts[id] = { x: p[0], y: p[1] }; return id; }
  node(p: Pt, w: number): NodeId { const id = this.ids(this.doc.nodes, 'n'); this.doc.nodes[id] = { x: p[0], y: p[1], w }; return id; }
  area(kind: OtherAreaKind, pts: Pt[]): AreaId { const id = this.ids(this.doc.areas, 'a'); this.doc.areas[id] = { kind, verts: pts.map(p => this.vert(p)) }; return id; }
  road(nodes: NodeId[], kind: RoadKind, closed: boolean): void { this.doc.roads[this.ids(this.doc.roads, 'r')] = { nodes, kind, closed }; }
  icon(type: IconType, x: number, y: number, fixed?: boolean): void {
    this.doc.icons[this.ids(this.doc.icons, 'i')] = fixed ? { type, x, y, fixed } : { type, x, y };
  }
}

/** Points every `step` metres around a closed ring. */
function aroundRing(ring: readonly Pt[], step: number): Pt[] {
  const closed = ring.concat([ring[0] as Pt]), cum = cumLengths(closed), len = cum[cum.length - 1] as number, out: Pt[] = [];
  for (let s = 0; s < len; s += step) out.push(pointAlong(closed, cum, s));
  return out;
}

/** Builds the plot, entrance, plaza, roads, water and back of house (prototype generatePlan). */
function layOut(b: Builder, opts: GenerateOptions, r: Rng): void {
  const doc = b.doc, M = opts.size;
  const shape = makePlot(r, opts.plot, M, opts.seed);
  doc.plot = { verts: shape.pts.map(p => b.vert(p)), smooth: shape.smooth };
  const pp = plotRing(doc) as Pt[], C = centroid(pp), pA = polyArea(pp), [bx0, by0, bx1, by1] = bbox(pp);
  const ENT = placeEntrance(pp, M);
  b.icon('entrance', ENT.x + ENT.ix * 6, ENT.y + ENT.iy * 6, true);
  const plazaA = Math.max(1600, pA * 0.022), plazaR = Math.sqrt(plazaA / Math.PI);
  const pc: Pt = [ENT.x + ENT.ix * (plazaR + 12), ENT.y + ENT.iy * (plazaR + 12)];
  b.area('plaza', blob(r, pc[0], pc[1], plazaA, 9, 0.25));
  const layout = opts.layout, water = opts.water;
  const margin = clamp(0.13 * Math.min(bx1 - bx0, by1 - by0), 28, 80), rx = (bx1 - bx0) / 2 * 0.66, ry = (by1 - by0) / 2 * 0.66;
  const loops = layout === 'eight'
    ? [makeLoop(r, [C[0] - rx * 0.5, C[1] - ry * 0.05], rx * 0.52, ry * 0.95, pp, margin, pc), makeLoop(r, [C[0] + rx * 0.5, C[1] - ry * 0.05], rx * 0.52, ry * 0.95, pp, margin, pc)]
    : [makeLoop(r, [C[0], C[1] - ry * 0.05], rx, ry, pp, margin, pc)];

  // lake, in the middle of the loop or on its waterfront; loop points are pushed out of it
  let lake: { pts: Pt[]; ring: Pt[] } | null = null;
  if (water === 'lake' || water === 'both') {
    const la = pA * (0.05 + r() * 0.04), lr = Math.sqrt(la / Math.PI), L0 = loops[0] as { pts: Pt[]; plazaIdx: number }, lc = centroid(L0.pts), cands: Pt[] = [];
    const waterfront = layout !== 'loop' || r() < 0.55;
    for (let t = 0; t < 6; t++) {
      if (!waterfront) { cands.push([lc[0] + (r() - 0.5) * rx * 0.3, lc[1] + (r() - 0.5) * ry * 0.3]); continue; }
      const L = loops[Math.floor(r() * loops.length)] as { pts: Pt[]; plazaIdx: number }, i = Math.floor(r() * L.pts.length);
      if (i === L.plazaIdx) continue;
      const p = L.pts[i] as Pt, c = centroid(L.pts), dx = c[0] - p[0], dy = c[1] - p[1], l = Math.hypot(dx, dy) || 1;
      cands.push([p[0] + dx / l * (lr + 9), p[1] + dy / l * (lr + 9)]);
    }
    const lcn = cands[0] ?? lc, pts = blob(r, lcn[0], lcn[1], la, 10, 0.4), id = b.area('lake', pts);
    lake = { pts, ring: areaRing(doc, id) };
    for (const L of loops) L.pts.forEach((p, i) => { if (i !== L.plazaIdx) pushOutOf(p, (lake as { ring: Pt[] }).ring, 9); });
  }

  // main loops: closed roads that start at the plaza dot, which every road shares
  const plazaNode = b.node(pc, 8);
  const loopNodes = loops.map(L => {
    const nodes = L.pts.map((p, i) => (i === L.plazaIdx ? plazaNode : b.node(p, 8)));
    b.road(nodes.slice(L.plazaIdx).concat(nodes.slice(0, L.plazaIdx)), 'main', true);
    return nodes;
  });
  if (layout === 'cross') {
    const L = loops[0] as { pts: Pt[]; plazaIdx: number }, dPc = (p: Pt) => Math.hypot(p[0] - pc[0], p[1] - pc[1]);
    let far = 0;
    L.pts.forEach((p, i) => { if (dPc(p) > dPc(L.pts[far] as Pt)) far = i; });
    const q = L.pts[far] as Pt, len = dPc(q), ux = (q[0] - pc[0]) / len, uy = (q[1] - pc[1]) / len;
    const mids = [0.33, 0.66].map(f => {
      const o = (r() - 0.5) * 0.22 * len, m: Pt = [pc[0] + (q[0] - pc[0]) * f - uy * o, pc[1] + (q[1] - pc[1]) * f + ux * o];
      return lake ? pushOutOf(m, lake.ring, 8) : m;
    });
    b.road([plazaNode, ...mids.map(m => b.node(m, 6)), (loopNodes[0] as NodeId[])[far] as NodeId], 'cross', false);
  }

  // river, kept away from the plaza and the entrance; it ends in the lake if there is one
  if (water === 'river' || water === 'both') {
    for (let att = 0; att < 40; att++) {
      const a = r() * Math.PI * 2, d0 = leaveDist(pp, C, Math.cos(a), Math.sin(a)) + 25, start: Pt = [C[0] + Math.cos(a) * d0, C[1] + Math.sin(a) * d0];
      let end: Pt;
      if (lake) end = centroid(lake.pts);
      else { const bb = a + Math.PI + (r() - 0.5) * 0.8, d1 = leaveDist(pp, C, Math.cos(bb), Math.sin(bb)) + 25; end = [C[0] + Math.cos(bb) * d1, C[1] + Math.sin(bb) * d1]; }
      const L = Math.hypot(end[0] - start[0], end[1] - start[1]) || 1, ux = (end[0] - start[0]) / L, uy = (end[1] - start[1]) / L, pts: Pt[] = [start];
      for (let i = 1; i <= 4; i++) {
        const base: Pt = [start[0] + (end[0] - start[0]) * i / 5, start[1] + (end[1] - start[1]) * i / 5];
        const cand = [0, 1, 2].map(() => { const o = (r() - 0.5) * 0.26 * L; return [base[0] - uy * o, base[1] + ux * o] as Pt; });
        pts.push(cand[0] as Pt);
      }
      pts.push(end);
      const poly = spline(pts, false), w = 9 + r() * 6;
      const ok = poly.every(q => Math.hypot(q[0] - pc[0], q[1] - pc[1]) > plazaR + w / 2 + 14 && Math.hypot(q[0] - ENT.x, q[1] - ENT.y) > 40);
      if (ok || att === 39) { doc.rivers[b.ids(doc.rivers, 'w')] = { pts, w }; break; }
    }
  }
  b.road([b.node([ENT.x + ENT.ix * 4, ENT.y + ENT.iy * 4], 8), plazaNode], 'entrance', false);

  // back of house on the plot edge, away from the entrance
  const bohA = Math.max(800, pA * 0.012), bohR = Math.sqrt(bohA / Math.PI);
  const roads = Object.values(doc.roads).map(rd => { const p = roadPoly(doc, rd); return { poly: p.pts, w: Math.max(...p.widths) }; });
  const rivers = Object.values(doc.rivers).map(rv => ({ poly: riverPoly(rv.pts), w: rv.w }));
  const edge = aroundRing(pp, 12).map(p => ({ p, d: Math.hypot(p[0] - ENT.x, p[1] - ENT.y) })).sort((a2, b2) => b2.d - a2.d);
  for (const { p } of edge) {
    const dx = C[0] - p[0], dy = C[1] - p[1], l = Math.hypot(dx, dy) || 1, c: Pt = [p[0] + dx / l * (bohR + 8), p[1] + dy / l * (bohR + 8)];
    if (!pip(pp, c[0], c[1])) continue;
    if (roads.some(q => distPoly(q.poly, c[0], c[1], false) < bohR + q.w / 2 + 6)) continue;
    if (rivers.some(q => distPoly(q.poly, c[0], c[1], false) < bohR + q.w / 2 + 6)) continue;
    if (lake && (pip(lake.ring, c[0], c[1]) || distPoly(lake.ring, c[0], c[1], true) < bohR + 6)) continue;
    b.area('backOfHouse', blob(r, c[0], c[1], bohA, 8, 0.2));
    b.icon('staff', c[0] - bohR * 0.4, c[1], true);
    b.icon('vet', c[0] + bohR * 0.4, c[1], true);
    break;
  }
}

/**
 * Generates a whole plan from the wizard's choices (TD §7.1): plot and entrance, plaza, main loop(s), lake,
 * cross path, river, entrance spur, back of house, shops, then packed zones for the picked species, with regions
 * following the chosen themes in walking order. Picks that don't fit (or whose species is unknown) come back in
 * `unplaced`; nothing is dropped silently.
 */
export function generatePlan(opts: GenerateOptions, species: Species[], themes: Theme[]): { doc: PlanDoc; unplaced: SpeciesPick[] } {
  const r = rng(opts.seed), ids = idMaker(), doc = emptyPlan(opts.size);
  doc.seed = opts.seed;
  doc.settings = { extraSpacePct: opts.extraSpacePct };
  const b = new Builder(doc, ids);
  layOut(b, opts, r);
  placeFacilities(doc, r, shopCount(opts.size), ids);

  const byId = new Map(species.map(s => [s.id, s]));
  const unplaced: SpeciesPick[] = [], known: { pick: SpeciesPick; sp: Species; count: number }[] = [];
  for (const pick of opts.speciesPicks) {
    const sp = byId.get(pick.speciesId);
    if (!sp) unplaced.push({ ...pick });
    else known.push({ pick, sp, count: clamp(Math.round(pick.count) || 1, 1, MAX_COUNT) });
  }
  const needs = known.map(k => speciesNeeds(k.sp, k.count, opts.extraSpacePct));
  const grouping = groupPicks(known.map(k => k.sp), needs.map(n => n.water), opts.themes, themes);
  const items: ZoneItem[] = known.map((k, i) => ({
    speciesId: k.sp.id, count: k.count, kind: k.sp.kind, group: grouping.groupOf[i] as number, need: needs[i] as { area: number; water: number },
  }));
  const placed = fillZones(doc, items, grouping.groups, grouping.waterGroup, r, ids);
  placed.forEach((id, i) => { if (!id) unplaced.push({ ...(known[i] as { pick: SpeciesPick }).pick }); });
  return { doc, unplaced };
}
